import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "../../components/UI/Modal";
import { useDispatch, useSelector } from "react-redux";
import { getUserById } from "../../actions";
import DetailsContent from "./DetailsContent";
import SubscribersContent from "./SubscribersContent";
import UserProductContainer from "./UserProductContainer";
import UserHistoryItems from "./UserHistoryItems";
import { clearUserInfoCart } from "../../reducers/user.reducer";
import EditRoleUserBlock from "./EditRoleUserBlock";
import EditUserProfileBlock from "./EditUserProfileBlock";
import UserSecurityBlock from "./UserSecurityBlock";
import UserBillingBlock from "./UserBillingBlock";
import ButtonContainer from "./ButtonContainer";
import UserConfirmDialog from "./UserConfirmDialog";
import Loader from "../../components/Loader";

const EMPTY_GUARD = null;

const RenderModalContent = ({
    handleCloseUserDetailsModal,
    userDetailModal,
    setTypeModal,
    userDetails = {},
    currentId,
    typeModal,
    isSuperAdmin,
}) => {
    const { cart, desires, loading, message } = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const [editGuard, setEditGuard] = useState(EMPTY_GUARD);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const [guardBusy, setGuardBusy] = useState(false);

    const emailVerified = !!(userDetails.isActivated || userDetails.emailVerifiedAt);
    const canManage = isSuperAdmin && userDetails.role !== "superAdmin";

    useEffect(() => {
        if (
            userDetailModal &&
            currentId &&
            String(currentId) !== String(userDetails?._id || "")
        ) {
            dispatch(getUserById(currentId));
        }
    }, [userDetailModal, currentId, userDetails?._id, dispatch]);

    useEffect(() => {
        if (typeModal === "") dispatch(clearUserInfoCart());
    }, [typeModal, dispatch]);

    useEffect(() => {
        setEditGuard(null);
        setPendingNavigation(null);
        setGuardBusy(false);
    }, [currentId]);

    useEffect(() => {
        if (!editGuard?.isDirty) return undefined;

        const beforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };

        window.addEventListener("beforeunload", beforeUnload);
        return () => window.removeEventListener("beforeunload", beforeUnload);
    }, [editGuard?.isDirty]);

    const registerGuard = useCallback((guard) => {
        setEditGuard(guard || null);
    }, []);

    const performNavigation = useCallback((navigation) => {
        if (!navigation) return;

        if (navigation.kind === "close") {
            handleCloseUserDetailsModal?.();
            return;
        }

        if (navigation.kind === "section") {
            setTypeModal(navigation.type || "");
        }
    }, [handleCloseUserDetailsModal, setTypeModal]);

    const requestNavigation = useCallback((nextType) => {
        if (String(nextType || "") === String(typeModal || "")) return;

        const navigation = { kind: "section", type: nextType || "" };

        if (editGuard?.isDirty) {
            setPendingNavigation(navigation);
            return;
        }

        performNavigation(navigation);
    }, [editGuard?.isDirty, performNavigation, typeModal]);

    const requestClose = useCallback(() => {
        const navigation = { kind: "close" };

        if (editGuard?.isDirty) {
            setPendingNavigation(navigation);
            return;
        }

        performNavigation(navigation);
    }, [editGuard?.isDirty, performNavigation]);

    const discardAndContinue = async () => {
        const navigation = pendingNavigation;
        setGuardBusy(true);

        try {
            await Promise.resolve(editGuard?.onDiscard?.());
            setPendingNavigation(null);
            setEditGuard(null);
            performNavigation(navigation);
        } finally {
            setGuardBusy(false);
        }
    };

    const saveAndContinue = async () => {
        if (!editGuard?.onSave || editGuard?.canSave === false) return;

        const navigation = pendingNavigation;
        setGuardBusy(true);

        try {
            const saved = await Promise.resolve(editGuard.onSave());
            setPendingNavigation(null);

            if (saved) {
                setEditGuard(null);
                performNavigation(navigation);
            }
        } finally {
            setGuardBusy(false);
        }
    };

    const renderContent = () => {
        switch (typeModal) {
            case "editProfile":
                return (
                    <EditUserProfileBlock
                        userDetails={userDetails}
                        setTypeModal={setTypeModal}
                        isSuperAdmin={isSuperAdmin}
                        registerGuard={registerGuard}
                        onNavigate={requestNavigation}
                    />
                );
            case "security":
                return (
                    <UserSecurityBlock
                        userDetails={userDetails}
                        registerGuard={registerGuard}
                    />
                );
            case "editRoleUser":
                return (
                    <EditRoleUserBlock
                        userDetails={userDetails}
                        setTypeModal={setTypeModal}
                        isSuperAdmin={isSuperAdmin}
                        registerGuard={registerGuard}
                        onNavigate={requestNavigation}
                    />
                );
            case "subscribers":
                return <SubscribersContent userDetails={userDetails} />;
            case "cart":
                return (
                    <UserProductContainer
                        typeModal={typeModal}
                        userId={currentId}
                        loading={loading}
                        message={message}
                        cart={cart}
                    />
                );
            case "desires":
                return (
                    <UserProductContainer
                        typeModal={typeModal}
                        userId={currentId}
                        desires={desires}
                        loading={loading}
                        message={message}
                    />
                );
            case "productHistory":
                return (
                    <UserHistoryItems
                        userId={currentId}
                        loading={loading}
                        message={message}
                    />
                );
            case "billing":
                return (
                    <UserBillingBlock
                        userDetails={userDetails}
                        registerGuard={registerGuard}
                    />
                );
            default:
                return (
                    <DetailsContent
                        userDetails={userDetails}
                        isSuperAdmin={isSuperAdmin}
                        onNavigate={requestNavigation}
                    />
                );
        }
    };

    const loaded = !!userDetails?._id && String(userDetails._id) === String(currentId || "");
    const fullName = [userDetails.firstName, userDetails.lastName].filter(Boolean).join(" ").trim() || "Пользователь";

    const confirmTitle = pendingNavigation?.kind === "close"
        ? (editGuard?.title || "Закрыть без сохранения?")
        : "Перейти в другой раздел?";

    const confirmText = pendingNavigation?.kind === "close"
        ? (editGuard?.text || "Есть несохранённые изменения.")
        : "В текущем разделе есть несохранённые изменения. Сначала выбери, что с ними сделать.";

    return (
        <>
            <Modal
                handleClose={requestClose}
                modalTitle={loaded ? `Управление: ${fullName}` : "Профиль пользователя"}
                show={userDetailModal}
                size="xl"
                disabledFunctional
                preventOutsideClose
                hideFooter
            >
                {!loaded ? (
                    <Loader typeSize="180px" />
                ) : (
                    <div className="userManagementWorkspace">
                        <aside className="userManagementWorkspace__nav">
                            <ButtonContainer
                                activeType={typeModal || ""}
                                onSelect={requestNavigation}
                                canManage={canManage}
                                emailVerified={emailVerified}
                            />
                        </aside>

                        <main className="userManagementWorkspace__content">
                            {renderContent()}
                        </main>
                    </div>
                )}
            </Modal>

            <UserConfirmDialog
                show={!!pendingNavigation}
                title={confirmTitle}
                text={confirmText}
                details={editGuard?.details || []}
                busy={guardBusy}
                dangerAction={{
                    label: pendingNavigation?.kind === "close"
                        ? "Закрыть без сохранения"
                        : "Перейти без сохранения",
                    onClick: discardAndContinue,
                }}
                secondaryAction={{
                    label: "Остаться здесь",
                    onClick: () => setPendingNavigation(null),
                }}
                primaryAction={editGuard?.onSave && editGuard?.canSave !== false ? {
                    label: pendingNavigation?.kind === "close"
                        ? "Сохранить и закрыть"
                        : "Сохранить и перейти",
                    onClick: saveAndContinue,
                } : null}
            />
        </>
    );
};

export default RenderModalContent;
